import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Wrench, FileText, Package, ChevronDown, ChevronUp,
  Plus, X, AlertCircle, Check, Loader2, Truck, Camera, Image, Eye, Send, Cloud, CloudOff, MapPin
} from 'lucide-react';
import { SignaturePad } from '../common/SignaturePad';
import { TMTicketPreview } from './TMTicketPreview';
import { CustomSelect } from '../common/CustomSelect';
import { CustomTimePicker } from '../common/CustomTimePicker';
import { openNativeCamera } from '../../utils/camera.utils';
import { isIOS } from '../../utils/platform.utils';
import { offlineService } from '../../services/offline.service';
import { syncService } from '../../services/sync.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Rental companies list (same as TruckDriverDashboard)
const RENTAL_COMPANIES = [
  'Kelly Tractor',
  'United Rentals',
  'Synergy',
  'Sunbelt',
  'Nortrax',
  'Alta',
  'Other'
];

// Equipment types list (same as TruckDriverDashboard)
const EQUIPMENT_TYPES = [
  'Excavator',
  'Mini Excavator',
  'Skid Steer',
  'Backhoe',
  'Dozer',
  'Loader',
  'Grader',
  'Paver',
  'Roller',
  'Dump Truck',
  'Flatbed',
  'Crane',
  'Forklift',
  'Scissor Lift',
  'Boom Lift',
  'Generator',
  'Compressor',
  'Light Tower',
  'Trailer',
  'Other'
];

// Initial data for editing an unsigned ticket
interface TicketInitialData {
  ticket_id: number;
  ticket_number: string;
  superintendent_name: string;
  superintendent_email: string;
  work_description: string;
  notes: string;
  start_time: string;
  stop_time: string;
  gc_name: string;
  employees: Array<{ employee_id: number; first_name: string; last_name: string; role: string; hours_worked: number }>;
  equipment: Array<{
    equipment_id: number | null;
    equipment_name: string;
    equipment_number: string;
    equipment_type: string;
    hours_used: number;
    is_rental: boolean;
    rental_company?: string;
    rental_equipment_type?: string;
    is_mobilized: boolean;
  }>;
  materials: Array<{ material_type: 'import' | 'export'; description: string; quantity: number; unit: string }>;
  dumpsters: Array<{ dumpster_type: string; quantity: number }>;
}

interface TMTicketFormProps {
  jobId: number;
  jobName: string;
  employeeId: number;
  language: 'en' | 'es';
  onSubmit: (ticketNumber: string) => void;
  onCancel: () => void;
  initialData?: TicketInitialData;  // For editing unsigned tickets
}

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
  role: string;
  hours_worked: number;
}

interface SelectedEmployee {
  employee_id: number;
  first_name: string;
  last_name: string;
  role: string;
  hours_worked: number;
  selected: boolean;
  tm_hours: number | string;
}

interface CompanyEquipment {
  equipment_id: number;
  equipment_name: string;
  equipment_type?: string;
  equipment_number?: string;
  bradanna_equipment_number?: string; // From all equipment API
  gps_hours?: number; // GPS-detected runtime today (suggested hours)
  is_mobilized?: boolean; // Hauled to the job today per EQUIPMENT_MOVEMENTS
}

interface EquipmentEntry {
  id: string; // Unique ID for React key
  equipment_id: number | null;
  equipment_name: string;
  equipment_number: string;
  equipment_type?: string;
  hours_used: number | string;
  is_rental: boolean;
  rental_company: string;
  rental_equipment_type: string;
  is_mobilized: boolean;
  isManual: boolean;
  gps_hours?: number; // GPS-detected runtime today; hours_used starts from this but stays editable
}

interface MaterialEntry {
  id: string;
  material_type: 'import' | 'export';
  description: string;
  custom_description: string;
  quantity: number;
  unit: string;
}

interface DumpsterEntry {
  id: string;
  dumpster_type: 'Concrete' | 'Dirt' | 'Vegetation' | 'C&D';
  size: '20' | '30';
  quantity: number;
}

// Dumpster types with default sizes
const DUMPSTER_TYPES = [
  { type: 'Concrete', defaultSize: '20' as const },
  { type: 'Dirt', defaultSize: '20' as const },
  { type: 'Vegetation', defaultSize: '30' as const },
  { type: 'C&D', defaultSize: '30' as const }
];

// Photo with background upload tracking
interface PhotoWithUpload {
  file: File;
  previewUrl: string;
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed';
  uploadedUrl?: string;
  thumbnailUrl?: string;
}

const translations = {
  en: {
    employeeHours: 'Employee Hours',
    loadingEmployees: 'Loading employees...',
    noEmployees: 'No employees clocked in at this job today',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    hours: 'Hours',
    totalEmployeeHours: 'Total Employee Hours',
    equipment: 'Equipment',
    loadingEquipment: 'Loading equipment...',
    availableHours: 'Available Hours',
    addEquipment: 'Add Equipment',
    addRental: 'Add Rental',
    searchEquipment: 'Search equipment by name or number...',
    noEquipmentFound: 'No equipment found',
    equipmentNumber: 'Equipment #',
    equipmentName: 'Equipment Name',
    rentalCompany: 'Rental Company',
    equipmentType: 'Equipment Type',
    gpsSuggested: 'GPS detected',
    mobilized: 'Mobilized Today',
    mobilizedHint: 'Delivered TODAY for this T&M ticket?',
    remove: 'Remove',
    notes: 'Notes',
    notesPlaceholder: 'Add any additional notes about this T&M ticket...',
    materials: 'Materials',
    imports: 'Imports',
    exports: 'Exports',
    addImport: 'Add Import',
    addExport: 'Add Export',
    description: 'Description',
    quantity: 'Quantity',
    unit: 'Unit',
    loads: 'Loads',
    cubicYards: 'CY',
    tons: 'Tons',
    selectMaterial: 'Select material...',
    customMaterial: 'Enter material description...',
    dumpsters: 'Dumpsters',
    addDumpster: 'Add Dumpster',
    dumpsterType: 'Type',
    dumpsterSize: 'Size',
    yards: 'Yard',
    superintendentSignature: 'Superintendent Signature',
    superintendentName: 'Superintendent Name',
    ifKnown: 'if known',
    superintendentEmail: 'Superintendent Email',
    signatureRequired: 'Signature required',
    submit: 'Submit T&M Ticket',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    reviewForSignature: 'Review for Signature',
    noAvailableHours: 'No Available Hours',
    validationError: 'Please fix the errors below',
    equipmentExceedsEmployee: 'Equipment hours cannot exceed employee hours',
    signatureRequiredError: 'Superintendent signature is required',
    nameRequiredError: 'Superintendent name is required',
    emailRequiredError: 'Superintendent email is required',
    invalidEmailError: 'Please enter a valid email address',
    noEmployeesSelectedError: 'Please select at least one employee',
    successMessage: 'T&M Ticket submitted successfully!',
    selectEquipment: 'Select equipment...',
    companyEquipment: 'Company Equipment',
    rentalEquipment: 'Rental Equipment',
    manualEntry: 'Manual Entry',
    selectRentalCompany: 'Select rental company...',
    selectEquipmentType: 'Select equipment type...',
    other: 'Other',
    customRentalCompany: 'Enter company name...',
    customEquipmentType: 'Enter equipment type...',
    photos: 'Photos',
    addPhoto: 'Add Photo',
    receiptsInvoices: 'Receipts/Invoices',
    jobSitePhotos: 'Job Site Photos',
    noPhotos: 'No photos added',
    tapToAddPhotos: 'Tap to add photos',
    selectMultiple: 'Select from camera or gallery',
    // New T&M enhancement fields
    workDescription: 'Scope of Work',
    workDescriptionPlaceholder: 'Describe the work performed...',
    aiSuggest: 'AI Suggest',
    translating: 'Translating...',
    gcName: 'General Contractor',
    pmEmail: 'Project Manager Email',
    noOneToSign: 'No one on site to sign',
    unsignedReason: 'Reason',
    selectReason: 'Select reason...',
    notAvailable: 'Not available',
    leftJobsite: 'Left jobsite',
    willSignLater: 'Will sign later',
    sendRemoteLink: 'Send signing link via email',
    startTime: 'Start Time',
    stopTime: 'Stop Time',
    addLaborer: 'Add General Laborer'
  },
  es: {
    employeeHours: 'Horas de Empleados',
    loadingEmployees: 'Cargando empleados...',
    noEmployees: 'No hay empleados fichados en este trabajo hoy',
    selectAll: 'Seleccionar Todos',
    deselectAll: 'Deseleccionar Todos',
    hours: 'Horas',
    totalEmployeeHours: 'Total Horas de Empleados',
    equipment: 'Equipo',
    loadingEquipment: 'Cargando equipo...',
    availableHours: 'Horas Disponibles',
    addEquipment: 'Agregar Equipo',
    addRental: 'Agregar Alquiler',
    searchEquipment: 'Buscar equipo por nombre o número...',
    noEquipmentFound: 'No se encontró equipo',
    equipmentNumber: 'Equipo #',
    equipmentName: 'Nombre del Equipo',
    rentalCompany: 'Compañía de Alquiler',
    equipmentType: 'Tipo de Equipo',
    gpsSuggested: 'Detectado por GPS',
    mobilized: 'Movilizado Hoy',
    mobilizedHint: '¿Entregado HOY para este boleto T&M?',
    remove: 'Eliminar',
    notes: 'Notas',
    notesPlaceholder: 'Agregue notas adicionales sobre este boleto de T&M...',
    materials: 'Materiales',
    imports: 'Importaciones',
    exports: 'Exportaciones',
    addImport: 'Agregar Importación',
    addExport: 'Agregar Exportación',
    description: 'Descripción',
    quantity: 'Cantidad',
    unit: 'Unidad',
    loads: 'Cargas',
    cubicYards: 'YC',
    tons: 'Toneladas',
    selectMaterial: 'Seleccionar material...',
    customMaterial: 'Ingrese descripción del material...',
    dumpsters: 'Contenedores',
    addDumpster: 'Agregar Contenedor',
    dumpsterType: 'Tipo',
    dumpsterSize: 'Tamaño',
    yards: 'Yardas',
    superintendentSignature: 'Firma del Superintendente',
    superintendentName: 'Nombre del Superintendente',
    ifKnown: 'si se conoce',
    superintendentEmail: 'Correo del Superintendente',
    signatureRequired: 'Firma requerida',
    submit: 'Enviar Boleto T&M',
    submitting: 'Enviando...',
    cancel: 'Cancelar',
    reviewForSignature: 'Revisar para Firma',
    noAvailableHours: 'Sin Horas Disponibles',
    validationError: 'Por favor corrija los errores abajo',
    equipmentExceedsEmployee: 'Las horas de equipo no pueden exceder las horas de empleados',
    signatureRequiredError: 'La firma del superintendente es requerida',
    nameRequiredError: 'El nombre del superintendente es requerido',
    emailRequiredError: 'El correo del superintendente es requerido',
    invalidEmailError: 'Por favor ingrese un correo válido',
    noEmployeesSelectedError: 'Por favor seleccione al menos un empleado',
    successMessage: '¡Boleto T&M enviado exitosamente!',
    selectEquipment: 'Seleccionar equipo...',
    companyEquipment: 'Equipo de la Compañía',
    rentalEquipment: 'Equipo de Alquiler',
    manualEntry: 'Entrada Manual',
    selectRentalCompany: 'Seleccionar compañía...',
    selectEquipmentType: 'Seleccionar tipo de equipo...',
    other: 'Otro',
    customRentalCompany: 'Ingrese nombre de compañía...',
    customEquipmentType: 'Ingrese tipo de equipo...',
    photos: 'Fotos',
    addPhoto: 'Agregar Foto',
    receiptsInvoices: 'Recibos/Facturas',
    jobSitePhotos: 'Fotos del Sitio',
    noPhotos: 'No hay fotos agregadas',
    tapToAddPhotos: 'Toque para agregar fotos',
    selectMultiple: 'Seleccionar de cámara o galería',
    // New T&M enhancement fields
    workDescription: 'Alcance del Trabajo',
    workDescriptionPlaceholder: 'Describa el trabajo realizado...',
    aiSuggest: 'Sugerir IA',
    translating: 'Traduciendo...',
    gcName: 'Contratista General',
    pmEmail: 'Correo del Gerente de Proyecto',
    noOneToSign: 'No hay nadie para firmar',
    unsignedReason: 'Razón',
    selectReason: 'Seleccione razón...',
    notAvailable: 'No disponible',
    leftJobsite: 'Se fue del sitio',
    willSignLater: 'Firmará después',
    sendRemoteLink: 'Enviar enlace para firmar',
    startTime: 'Hora de Inicio',
    stopTime: 'Hora de Fin',
    addLaborer: 'Agregar Jornalero'
  }
};

const UNIT_OPTIONS = ['loads', 'cy', 'tons', 'each', 'lf', 'sf', 'sy'];

// Standard import materials (bringing TO site)
const IMPORT_MATERIALS = [
  'Fill Dirt',
  'Base / Limerock',
  '57 Stone',
  '89 Stone',
  'Rip Rap',
  'Construction Entrance Stone',
  'Asphalt',
  'Asphalt Millings',
  'Concrete',
  'Rock',
  'Sand',
  'Gravel',
  'Topsoil',
  'Pipe',
  'Other'
];

// Standard export materials (hauling FROM site)
// Note: Dumpsters are now handled in the dedicated Dumpsters section
const EXPORT_MATERIALS = [
  'Fill Dirt',
  'Baserock',
  '57 Stone',
  '89 Stone',
  'Rip Rap',
  'Construction Entrance Stone',
  'Asphalt',
  'Asphalt Millings',
  'Spoils',
  'Trash',
  'Other'
];

export const TMTicketForm: React.FC<TMTicketFormProps> = ({
  jobId,
  jobName,
  employeeId,
  language,
  onSubmit,
  onCancel,
  initialData
}) => {
  const t = translations[language];

  // Section expand states - all sections open by default
  const [expandedSections, setExpandedSections] = useState({
    employees: true,
    equipment: true,
    notes: true,
    materials: true,
    photos: true,     // Open by default
    signature: true
  });

  // Preview mode state
  const [showPreview, setShowPreview] = useState(false);

  // Submitter info (loaded from localStorage)
  const [submitterName, setSubmitterName] = useState('');
  const [jobNumber, setJobNumber] = useState('');

  // Data states
  const [employees, setEmployees] = useState<SelectedEmployee[]>([]);
  const [companyEquipment, setCompanyEquipment] = useState<CompanyEquipment[]>([]);
  const [equipmentEntries, setEquipmentEntries] = useState<EquipmentEntry[]>([]);
  const [materials, setMaterials] = useState<MaterialEntry[]>([]);
  const [dumpsters, setDumpsters] = useState<DumpsterEntry[]>([]);
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [photoUploads, setPhotoUploads] = useState<PhotoWithUpload[]>([]); // Track background uploads

  // New T&M enhancement fields
  const [workDescription, setWorkDescription] = useState(initialData?.work_description || '');
  const [gcName, setGcName] = useState(initialData?.gc_name || '');
  const [pmEmail, setPmEmail] = useState('');
  const [noOneToSign, setNoOneToSign] = useState(false);
  const [unsignedReason, setUnsignedReason] = useState('Left jobsite');
  const [isTranslatingNotes, setIsTranslatingNotes] = useState(false);
  const [isTranslatingDescription, setIsTranslatingDescription] = useState(false);

  // Superintendent signature
  const [superintendentName, setSuperintendentName] = useState(initialData?.superintendent_name || '');
  const [superintendentEmail, setSuperintendentEmail] = useState(initialData?.superintendent_email || '');
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);

  // Start time - default to current time, will be updated to foreman's clock-in time
  const [startTime, setStartTime] = useState(() => {
    // If editing, use the ticket's start time
    if (initialData?.start_time) {
      const startDate = new Date(initialData.start_time);
      return startDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'America/New_York'
      });
    }
    const now = new Date();
    return now.toTimeString().slice(0, 5); // HH:MM format
  });

  // Stop time - optional. When set, every selected worker's hours are set to
  // the start→stop span (rounded up to the half hour). Lets the foreman put
  // the real quitting time on the ticket when the super signs early.
  const [stopTime, setStopTime] = useState(() => {
    if (initialData?.stop_time) {
      return new Date(initialData.stop_time).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'America/New_York'
      });
    }
    return '';
  });

  // Loading states
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Error states
  const [errors, setErrors] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [adjustedHoursMessage, setAdjustedHoursMessage] = useState<string | null>(null);

  // Equipment search states
  const [allEquipment, setAllEquipment] = useState<CompanyEquipment[]>([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');

  // LocalStorage draft key
  const DRAFT_KEY = `tm_ticket_draft_${jobId}`;

  // Helper to scroll input into view only when keyboard actually opens
  const scrollInputIntoView = useCallback((element: HTMLElement) => {
    // On iOS/Android, the visual viewport shrinks when keyboard opens
    // Only scroll if we detect the viewport height decreased significantly
    const initialHeight = window.visualViewport?.height || window.innerHeight;

    const checkAndScroll = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      // Keyboard typically takes 200-300px, so check for >100px decrease
      if (initialHeight - currentHeight > 100) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Check after keyboard animation completes
    setTimeout(checkAndScroll, 400);
  }, []);

  // Calculate totals
  const totalEmployeeHours = useMemo(() => {
    return employees
      .filter(e => e.selected)
      .reduce((sum, e) => {
        const hours = typeof e.tm_hours === 'string' ? parseFloat(e.tm_hours) || 0 : (e.tm_hours || 0);
        return sum + hours;
      }, 0);
  }, [employees]);

  const totalEquipmentHours = useMemo(() => {
    return equipmentEntries.reduce((sum, e) => {
      const hours = typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0);
      return sum + hours;
    }, 0);
  }, [equipmentEntries]);

  const availableHours = useMemo(() => {
    return Math.max(0, totalEmployeeHours - totalEquipmentHours);
  }, [totalEmployeeHours, totalEquipmentHours]);

  // Filter equipment for search - exclude already added equipment
  const filteredEquipment = useMemo(() => {
    if (!equipmentSearch.trim()) return [];
    const addedEquipmentIds = equipmentEntries
      .filter(e => e.equipment_id)
      .map(e => e.equipment_id);
    const searchLower = equipmentSearch.toLowerCase();
    return allEquipment.filter(eq =>
      !addedEquipmentIds.includes(eq.equipment_id) &&
      (eq.equipment_name?.toLowerCase().includes(searchLower) ||
       eq.equipment_number?.toLowerCase().includes(searchLower) ||
       eq.bradanna_equipment_number?.toLowerCase().includes(searchLower))
    ).slice(0, 10); // Limit to 10 results
  }, [equipmentSearch, allEquipment, equipmentEntries]);

  // Load submitter name from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('bfa_user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setSubmitterName(`${user.first_name || ''} ${user.last_name || ''}`.trim());
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Load employees and merge with any saved draft selections or initialData (for editing)
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const token = localStorage.getItem('bfa_token');
        // Pass foreman_id to get their clock-in time for default start time
        const response = await fetch(
          `${API_BASE_URL}/api/tm-tickets/job/${jobId}/employees?foreman_id=${employeeId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();

        if (result.success && result.data) {
          // Use foreman's clock-in time as default start time (if not editing)
          if (!initialData && result.foremanClockIn) {
            const clockInDate = new Date(result.foremanClockIn);
            const clockInTime = clockInDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
            setStartTime(clockInTime);
          }

          // Check if editing - merge with initialData employees
          if (initialData?.employees) {
            // Transform to selected employees, using initialData hours for matching employees
            const selectedEmployees = result.data.map((emp: Employee) => {
              const initialEmp = initialData.employees.find(e => e.employee_id === emp.employee_id);
              if (initialEmp) {
                // Use hours from the original ticket
                return {
                  ...emp,
                  selected: true,
                  tm_hours: initialEmp.hours_worked || emp.hours_worked || 0
                };
              }
              // Employee is clocked in but wasn't on original ticket - unselect by default
              return {
                ...emp,
                selected: false,
                tm_hours: emp.hours_worked || 0
              };
            });
            setEmployees(selectedEmployees);
          } else {
            // Check for saved draft to merge employee selections
            let savedSelections: any[] = [];
            try {
              const savedDraft = localStorage.getItem(DRAFT_KEY);
              if (savedDraft) {
                const draft = JSON.parse(savedDraft);
                savedSelections = draft.employeeSelections || [];
              }
            } catch (e) {
              console.error('Error reading draft for employee merge:', e);
            }

            // Transform to selected employees, merging with saved selections
            const selectedEmployees = result.data.map((emp: Employee) => {
              const savedEmp = savedSelections.find((s: any) => s.employee_id === emp.employee_id);
              if (savedEmp) {
                // Use saved selection and hours
                return {
                  ...emp,
                  selected: savedEmp.selected,
                  tm_hours: savedEmp.tm_hours
                };
              }
              // Default: selected with hours from API
              return {
                ...emp,
                selected: true,
                tm_hours: emp.hours_worked || 0
              };
            });
            setEmployees(selectedEmployees);
          }
        }
      } catch (error) {
        console.error('Error loading employees:', error);
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [jobId, DRAFT_KEY, employeeId, initialData]);

  // Load job details for superintendent info
  useEffect(() => {
    const loadJobDetails = async () => {
      try {
        const token = localStorage.getItem('bfa_token');
        let foundName = false;
        let foundEmail = false;

        // 1. First try job details
        const response = await fetch(
          `${API_BASE_URL}/api/jobs/${jobId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();

        if (result.success && result.data) {
          const job = result.data;
          // Extract job number for preview
          if (job.bradanna_job_number) {
            setJobNumber(job.bradanna_job_number);
          }
          // Set GC name if available
          if (job.contractor_name) {
            setGcName(job.contractor_name);
          }
          // Set PM email if available
          if (job.project_manager_email) {
            setPmEmail(job.project_manager_email);
          }
          // Preload superintendent info if available
          if (job.superintendent_name) {
            setSuperintendentName(job.superintendent_name);
            foundName = true;
          }
          // If job has contractor, try to get GC contact email
          if (job.contractor_id) {
            try {
              const gcResponse = await fetch(
                `${API_BASE_URL}/api/gc-contacts?contractor_id=${job.contractor_id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
              );
              const gcResult = await gcResponse.json();
              if (gcResult.success && gcResult.data && gcResult.data.length > 0) {
                // Use first contact's email if superintendent email not already set
                const contact = gcResult.data[0];
                if (contact.email && !foundEmail) {
                  setSuperintendentEmail(contact.email);
                  foundEmail = true;
                }
                if (contact.contact_name && !foundName) {
                  setSuperintendentName(contact.contact_name);
                  foundName = true;
                }
              }
            } catch (gcError) {
              console.error('Error loading GC contacts:', gcError);
            }
          }
        }

        // 2. If still missing info, try previous T&M tickets for this job
        if (!foundName || !foundEmail) {
          try {
            const prevResponse = await fetch(
              `${API_BASE_URL}/api/tm-tickets/job/${jobId}/superintendent`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const prevResult = await prevResponse.json();
            if (prevResult.success && prevResult.data) {
              if (prevResult.data.superintendent_name && !foundName) {
                setSuperintendentName(prevResult.data.superintendent_name);
              }
              if (prevResult.data.superintendent_email && !foundEmail) {
                setSuperintendentEmail(prevResult.data.superintendent_email);
              }
            }
          } catch (prevError) {
            console.error('Error loading previous superintendent info:', prevError);
          }
        }
      } catch (error) {
        console.error('Error loading job details:', error);
      }
    };

    loadJobDetails();
  }, [jobId]);

  // Load equipment and merge with saved draft data or initialData (for editing)
  useEffect(() => {
    const loadEquipment = async () => {
      try {
        const token = localStorage.getItem('bfa_token');

        // If editing, use initialData equipment instead of draft
        if (initialData?.equipment && initialData.equipment.length > 0) {
          // Transform initialData equipment to EquipmentEntry format
          const entries: EquipmentEntry[] = initialData.equipment.map((eq, index) => ({
            id: eq.is_rental ? `edit-rental-${index}` : `edit-company-${eq.equipment_id || index}`,
            equipment_id: eq.equipment_id,
            equipment_name: eq.equipment_name,
            equipment_number: eq.equipment_number || '',
            equipment_type: eq.equipment_type,
            hours_used: eq.hours_used || 0,
            is_rental: eq.is_rental || false,
            rental_company: eq.rental_company || '',
            rental_equipment_type: eq.rental_equipment_type || '',
            is_mobilized: eq.is_mobilized || false,
            isManual: eq.equipment_id === null
          }));
          setEquipmentEntries(entries);
          setLoadingEquipment(false);
          return;
        }

        // Check for saved draft FIRST
        let savedEquipmentEntries: EquipmentEntry[] = [];
        const savedDraft = localStorage.getItem(DRAFT_KEY);
        if (savedDraft) {
          try {
            const draft = JSON.parse(savedDraft);
            if (draft.equipmentEntries && Array.isArray(draft.equipmentEntries)) {
              savedEquipmentEntries = draft.equipmentEntries;
            }
          } catch (e) {
            console.error('Error parsing draft for equipment:', e);
          }
        }

        const response = await fetch(
          `${API_BASE_URL}/api/tm-tickets/job/${jobId}/equipment`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();

        if (result.success && result.data) {
          setCompanyEquipment(result.data.company || []);

          // Build entries from API, but MERGE with saved draft data
          const entries: EquipmentEntry[] = (result.data.company || []).map((eq: CompanyEquipment) => {
            // Check if we have saved data for this equipment
            const savedEntry = savedEquipmentEntries.find(
              (s) => s.equipment_id === eq.equipment_id || s.id === `company-${eq.equipment_id}`
            );

            if (savedEntry) {
              // Use saved values for hours, mobilization, etc.
              const savedHours = typeof savedEntry.hours_used === 'string'
                ? parseFloat(savedEntry.hours_used) || 0
                : (savedEntry.hours_used || 0);
              return {
                ...savedEntry,
                // Keep equipment_name/number from API in case they changed
                equipment_name: eq.equipment_name,
                equipment_number: eq.equipment_number || eq.bradanna_equipment_number || '',
                // Drafts saved before machines accumulated runtime hold 0 hours;
                // backfill from the fresh GPS suggestion so re-opened drafts
                // don't submit (and silently drop) zero-hour equipment
                hours_used: savedHours > 0 ? savedEntry.hours_used : (eq.gps_hours ?? 0),
                gps_hours: eq.gps_hours
              };
            }

            // No saved data - prefill hours from GPS runtime (editable suggestion)
            return {
              id: `company-${eq.equipment_id}`,
              equipment_id: eq.equipment_id,
              equipment_name: eq.equipment_name,
              equipment_number: eq.equipment_number || eq.bradanna_equipment_number || '',
              equipment_type: eq.equipment_type,
              hours_used: eq.gps_hours ?? 0,
              is_rental: false,
              rental_company: '',
              rental_equipment_type: '',
              is_mobilized: eq.is_mobilized ?? false,
              isManual: false,
              gps_hours: eq.gps_hours
            };
          });

          // Add rental equipment from API (with draft merge if exists)
          (result.data.rental || []).forEach((rental: any, index: number) => {
            const savedEntry = savedEquipmentEntries.find(
              (s) => s.id === `rental-${index}` && s.is_rental
            );

            if (savedEntry) {
              entries.push(savedEntry);
            } else {
              entries.push({
                id: `rental-${index}`,
                equipment_id: null,
                equipment_name: rental.rental_description || '',
                equipment_number: '',
                equipment_type: rental.rental_equipment_type,
                hours_used: 0,
                is_rental: true,
                rental_company: rental.rental_company,
                rental_equipment_type: rental.rental_equipment_type,
                is_mobilized: false,
                isManual: false
              });
            }
          });

          // IMPORTANT: Add any user-added entries from draft (manual adds, manual rentals)
          // These have IDs like 'manual-*' or 'rental-new-*' or were added via search
          savedEquipmentEntries.forEach((savedEntry) => {
            const isManualAdd = savedEntry.isManual ||
                                savedEntry.id.startsWith('manual-') ||
                                savedEntry.id.startsWith('rental-new-') ||
                                savedEntry.id.startsWith('search-');

            // Check if already added from API
            const alreadyAdded = entries.some((e) => e.id === savedEntry.id);

            if (isManualAdd && !alreadyAdded) {
              entries.push(savedEntry);
            }
          });

          setEquipmentEntries(entries);
        }
      } catch (error) {
        console.error('Error loading equipment:', error);
      } finally {
        setLoadingEquipment(false);
      }
    };

    loadEquipment();
  }, [jobId, DRAFT_KEY, initialData]);

  // Load all equipment for search
  useEffect(() => {
    const loadAllEquipment = async () => {
      try {
        const token = localStorage.getItem('bfa_token');
        const response = await fetch(
          `${API_BASE_URL}/api/equipment`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();
        if (result.success && result.data) {
          setAllEquipment(result.data);
        }
      } catch (error) {
        console.error('Error loading all equipment:', error);
      }
    };
    loadAllEquipment();
  }, []);

  // Convert file to base64 for localStorage storage
  const fileToBase64ForStorage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Convert base64 back to File
  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Initialize materials and dumpsters from initialData (for editing)
  useEffect(() => {
    if (initialData) {
      // Initialize materials from ticket data
      if (initialData.materials && initialData.materials.length > 0) {
        const materialEntries: MaterialEntry[] = initialData.materials.map((m, index) => ({
          id: `edit-material-${index}`,
          material_type: m.material_type,
          description: m.description,
          custom_description: '',
          quantity: m.quantity,
          unit: m.unit
        }));
        setMaterials(materialEntries);
      }

      // Initialize dumpsters from ticket data
      if (initialData.dumpsters && initialData.dumpsters.length > 0) {
        const dumpsterEntries: DumpsterEntry[] = initialData.dumpsters.map((d, index) => ({
          id: `edit-dumpster-${index}`,
          dumpster_type: d.dumpster_type as 'Concrete' | 'Dirt' | 'Vegetation' | 'C&D',
          size: '20', // Default size since not stored
          quantity: d.quantity
        }));
        setDumpsters(dumpsterEntries);
      }
    }
  }, [initialData]);

  // Load draft from localStorage on mount (skip if editing)
  // Note: Employee selections are merged in loadEmployees() to avoid race condition
  // Note: Equipment entries are merged in loadEquipment() to avoid race condition
  useEffect(() => {
    // Skip draft loading if we have initialData (editing mode)
    if (initialData) return;

    const loadDraft = async () => {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          if (draft.notes) setNotes(draft.notes);
          if (draft.workDescription) setWorkDescription(draft.workDescription);
          if (draft.materials) setMaterials(draft.materials);
          if (draft.dumpsters) setDumpsters(draft.dumpsters);
          if (draft.superintendentName) setSuperintendentName(draft.superintendentName);
          if (draft.superintendentEmail) setSuperintendentEmail(draft.superintendentEmail);
          if (draft.signatureBase64) setSignatureBase64(draft.signatureBase64);

          // Equipment entries are now merged in loadEquipment() to avoid race condition

          // Restore photos from base64
          if (draft.attachmentBase64s && Array.isArray(draft.attachmentBase64s)) {
            const restoredFiles = draft.attachmentBase64s.map((item: { data: string; name: string }, index: number) => {
              return base64ToFile(item.data, item.name || `photo-${index}.jpg`);
            });
            setAttachments(restoredFiles);
          }
        } catch (e) {
          console.error('Error loading draft:', e);
        }
      }
    };
    loadDraft();
  }, [DRAFT_KEY, initialData]);

  // Save draft to localStorage when key fields change (debounced for photos)
  useEffect(() => {
    const saveDraft = async () => {
      try {
        // Convert attachments to base64 for storage
        const attachmentBase64s = await Promise.all(
          attachments.map(async (file) => ({
            data: await fileToBase64ForStorage(file),
            name: file.name
          }))
        );

        // Save employee selections (only selected/hours, not full employee data)
        const employeeSelections = employees
          .filter(e => {
            const hours = typeof e.tm_hours === 'string' ? parseFloat(e.tm_hours) || 0 : (e.tm_hours || 0);
            return e.selected || hours > 0;
          })
          .map(e => ({
            employee_id: e.employee_id,
            selected: e.selected,
            tm_hours: e.tm_hours
          }));

        const draft = {
          notes,
          workDescription,
          materials,
          dumpsters,
          superintendentName,
          superintendentEmail,
          signatureBase64,
          employeeSelections,
          equipmentEntries,
          attachmentBase64s,
          timestamp: Date.now()
        };

        // Check size before saving (localStorage limit ~5MB)
        const draftString = JSON.stringify(draft);
        if (draftString.length < 4 * 1024 * 1024) { // 4MB limit for safety
          localStorage.setItem(DRAFT_KEY, draftString);
        } else {
          console.warn('Draft too large for localStorage, saving without photos');
          const draftWithoutPhotos = {
            ...draft,
            attachmentBase64s: [],
            signatureBase64: null // Also skip signature if too large
          };
          localStorage.setItem(DRAFT_KEY, JSON.stringify(draftWithoutPhotos));
        }
      } catch (e) {
        console.error('Error saving draft:', e);
      }
    };

    // Debounce save to avoid too many writes
    const timeoutId = setTimeout(saveDraft, 500);
    return () => clearTimeout(timeoutId);
  }, [notes, workDescription, materials, dumpsters, superintendentName, superintendentEmail, signatureBase64, employees, equipmentEntries, attachments, DRAFT_KEY]);

  // Clear draft on successful submit
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
  };

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Employee handlers
  const toggleEmployee = (employeeId: number) => {
    setEmployees(prev => prev.map(emp =>
      emp.employee_id === employeeId
        ? { ...emp, selected: !emp.selected }
        : emp
    ));
  };

  const toggleAllEmployees = (selected: boolean) => {
    setEmployees(prev => prev.map(emp => ({ ...emp, selected })));
  };

  // T&M hours are half-hour increments, rounded UP (2.1 -> 2.5). Mirrors backend.
  const roundUpHalf = (h: number): number => {
    if (!Number.isFinite(h) || h <= 0) return 0;
    return Math.ceil(h * 2 - 1e-9) / 2;
  };

  // Hours between start and stop 'HH:MM' strings, rounded up to a half; null if invalid
  const spanHours = (start: string, stop: string): number | null => {
    if (!start || !stop) return null;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = stop.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) return null;
    return roundUpHalf(mins / 60);
  };

  // Apply the start→stop span to every selected worker (and manual laborers)
  const applyStopTimeToHours = (start: string, stop: string) => {
    const hrs = spanHours(start, stop);
    if (hrs == null) return;
    setEmployees(prev => prev.map(emp =>
      (emp.selected || emp.employee_id < 0) ? { ...emp, tm_hours: hrs } : emp
    ));
  };

  const updateEmployeeHours = (employeeId: number, hours: number | string) => {
    setEmployees(prev => prev.map(emp =>
      emp.employee_id === employeeId
        ? { ...emp, tm_hours: hours }
        : emp
    ));
  };

  // Add a manual general laborer
  const addGeneralLaborer = () => {
    const newLaborer: SelectedEmployee = {
      employee_id: -Date.now(), // Negative ID to distinguish from real employees
      first_name: 'General',
      last_name: 'Laborer',
      role: 'laborer',
      hours_worked: 0,
      selected: true,
      tm_hours: 0
    };
    setEmployees(prev => [...prev, newLaborer]);
  };

  // Remove manual laborer
  const removeManualLaborer = (employeeId: number) => {
    setEmployees(prev => prev.filter(emp => emp.employee_id !== employeeId));
  };

  // Update manual laborer name
  const updateLaborerName = (employeeId: number, firstName: string, lastName: string) => {
    setEmployees(prev => prev.map(emp =>
      emp.employee_id === employeeId
        ? { ...emp, first_name: firstName, last_name: lastName }
        : emp
    ));
  };

  // Equipment handlers
  const updateEquipmentEntry = (id: string, field: keyof EquipmentEntry, value: any) => {
    setEquipmentEntries(prev => prev.map(entry =>
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const removeEquipmentEntry = (id: string) => {
    setEquipmentEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const addManualEquipment = () => {
    const newEntry: EquipmentEntry = {
      id: `manual-${Date.now()}`,
      equipment_id: null,
      equipment_name: '',
      equipment_number: '',
      hours_used: 0,
      is_rental: false,
      rental_company: '',
      rental_equipment_type: '',
      is_mobilized: false,
      isManual: true
    };
    setEquipmentEntries(prev => [...prev, newEntry]);
  };

  const addEquipmentFromSearch = (eq: CompanyEquipment) => {
    // Check if already added
    if (equipmentEntries.some(e => e.equipment_id === eq.equipment_id)) {
      return;
    }
    const newEntry: EquipmentEntry = {
      id: `search-${eq.equipment_id}`,
      equipment_id: eq.equipment_id,
      equipment_name: eq.equipment_name,
      equipment_number: eq.equipment_number || eq.bradanna_equipment_number || '',
      equipment_type: eq.equipment_type,
      hours_used: 0,
      is_rental: false,
      rental_company: '',
      rental_equipment_type: '',
      is_mobilized: false,
      isManual: false
    };
    setEquipmentEntries(prev => [...prev, newEntry]);
    setEquipmentSearch('');
  };

  const addRentalEquipment = () => {
    const newEntry: EquipmentEntry = {
      id: `rental-new-${Date.now()}`,
      equipment_id: null,
      equipment_name: '',
      equipment_number: '',
      hours_used: 0,
      is_rental: true,
      rental_company: '',
      rental_equipment_type: '',
      is_mobilized: false,
      isManual: true
    };
    setEquipmentEntries(prev => [...prev, newEntry]);
  };

  // Material handlers
  const addMaterial = (type: 'import' | 'export') => {
    const newMaterial: MaterialEntry = {
      id: `material-${Date.now()}`,
      material_type: type,
      description: '',
      custom_description: '',
      quantity: 1,  // Default to 1
      unit: 'loads'
    };
    setMaterials(prev => [...prev, newMaterial]);
  };

  const updateMaterial = (id: string, field: keyof MaterialEntry, value: any) => {
    setMaterials(prev => prev.map(mat =>
      mat.id === id ? { ...mat, [field]: value } : mat
    ));
  };

  const removeMaterial = (id: string) => {
    setMaterials(prev => prev.filter(mat => mat.id !== id));
  };

  // Dumpster handlers
  const addDumpster = (type: 'Concrete' | 'Dirt' | 'Vegetation' | 'C&D') => {
    // Check if a dumpster of this type already exists
    const existingDumpster = dumpsters.find(d => d.dumpster_type === type);
    if (existingDumpster) {
      // Increment quantity of existing dumpster
      setDumpsters(prev => prev.map(dump =>
        dump.id === existingDumpster.id
          ? { ...dump, quantity: dump.quantity + 1 }
          : dump
      ));
    } else {
      // Add new dumpster entry
      const dumpsterConfig = DUMPSTER_TYPES.find(d => d.type === type);
      const newDumpster: DumpsterEntry = {
        id: `dumpster-${Date.now()}`,
        dumpster_type: type,
        size: dumpsterConfig?.defaultSize || '20',
        quantity: 1
      };
      setDumpsters(prev => [...prev, newDumpster]);
    }
  };

  const updateDumpster = (id: string, field: keyof DumpsterEntry, value: any) => {
    setDumpsters(prev => prev.map(dump =>
      dump.id === id ? { ...dump, [field]: value } : dump
    ));
  };

  const removeDumpster = (id: string) => {
    setDumpsters(prev => prev.filter(dump => dump.id !== id));
  };

  // Photo compression for better upload performance
  const compressImage = useCallback((file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = document.createElement('img');

      img.onerror = () => reject(new Error('Failed to load image'));

      img.onload = () => {
        try {
          // Resize to max 1200px for good quality without huge files
          const maxDimension = 1200;
          let width = img.width;
          let height = img.height;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(img.src);

                if (blob) {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                } else {
                  resolve(file);
                }
              },
              'image/jpeg',
              0.85 // Good quality for receipts/photos
            );
          } else {
            resolve(file);
          }
        } catch (error) {
          console.error('Error during image compression:', error);
          resolve(file);
        }
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Upload photo to blob storage in background
  const uploadPhotoInBackground = async (file: File, previewUrl: string, retryCount: number = 0) => {
    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 60000;

    // Add to tracking array with 'uploading' status
    if (retryCount === 0) {
      const photoEntry: PhotoWithUpload = {
        file,
        previewUrl,
        uploadStatus: 'uploading'
      };
      setPhotoUploads(prev => [...prev, photoEntry]);
    }

    try {
      const token = localStorage.getItem('bfa_token');
      const formData = new FormData();
      formData.append('photo', file);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/upload-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success) {
        console.log(`Background upload complete: ${file.name} -> ${data.data.fullUrl}`);
        setPhotoUploads(prev => prev.map(p =>
          p.previewUrl === previewUrl
            ? { ...p, uploadStatus: 'uploaded' as const, uploadedUrl: data.data.fullUrl, thumbnailUrl: data.data.thumbnailUrl }
            : p
        ));
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error(`Background upload error: ${file.name}`, err.message || err);

      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying upload for ${file.name} in 2 seconds...`);
        setTimeout(() => {
          uploadPhotoInBackground(file, previewUrl, retryCount + 1);
        }, 2000);
      } else {
        console.log(`Pre-upload failed for ${file.name}, will upload at submit`);
        setPhotoUploads(prev => prev.map(p =>
          p.previewUrl === previewUrl
            ? { ...p, uploadStatus: 'pending' as const }
            : p
        ));
      }
    }
  };

  // Add photo and start background upload
  const addPhotoWithUpload = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setAttachments(prev => [...prev, file]);
    // Start background upload
    uploadPhotoInBackground(file, previewUrl);
  };

  // Photo attachment handlers
  const handleAddPhoto = async (useCamera: boolean = true) => {
    try {
      if (isIOS() && useCamera) {
        // iOS: Use native camera for camera mode
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            const compressed = await compressImage(file);
            addPhotoWithUpload(compressed);
          }
        });
      } else {
        // Android/Web or gallery mode: Use file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        if (useCamera) {
          input.capture = 'environment'; // Opens camera on Android
        }
        input.multiple = !useCamera; // Allow multiple only for gallery

        input.onchange = async (e: any) => {
          const files = Array.from(e.target?.files || []) as File[];
          // Compress each file and start background upload
          for (const f of files) {
            const compressed = await compressImage(f);
            addPhotoWithUpload(compressed);
          }
        };

        input.click();
      }
    } catch (error) {
      console.error('Error capturing photos:', error);
    }
  };

  const removeAttachment = (index: number) => {
    const fileToRemove = attachments[index];
    // Remove from photoUploads tracking
    setPhotoUploads(prev => prev.filter(p => p.file !== fileToRemove));
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: string[] = [];

    // Check employees selected
    const selectedEmployees = employees.filter(e => e.selected);
    if (selectedEmployees.length === 0) {
      newErrors.push(t.noEmployeesSelectedError);
    }

    // Check equipment hours don't exceed employee hours (with small tolerance for floating point)
    if (totalEquipmentHours > totalEmployeeHours + 0.05) {
      newErrors.push(t.equipmentExceedsEmployee);
    }

    // Check superintendent info — optional when no one is on site to sign
    // (unknown super/email should be submitted blank, not faked to pass validation)
    if (!noOneToSign && !superintendentName.trim()) {
      newErrors.push(t.nameRequiredError);
    }

    if (!noOneToSign && !superintendentEmail.trim()) {
      newErrors.push(t.emailRequiredError);
    } else if (superintendentEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(superintendentEmail)) {
        newErrors.push(t.invalidEmailError);
      }
    }

    if (!signatureBase64) {
      newErrors.push(t.signatureRequiredError);
    }

    setErrors(newErrors);
    if (newErrors.length > 0) {
      setShowErrorModal(true);
    }
    return newErrors.length === 0;
  }, [employees, totalEquipmentHours, totalEmployeeHours, superintendentName, superintendentEmail, signatureBase64, noOneToSign, t]);

  // Validation for preview (without signature requirement)
  const validateForPreview = useCallback((): boolean => {
    const newErrors: string[] = [];

    // Check employees selected
    const selectedEmployees = employees.filter(e => e.selected);
    if (selectedEmployees.length === 0) {
      newErrors.push(t.noEmployeesSelectedError);
    }

    // Check equipment hours don't exceed employee hours (with small tolerance for floating point)
    if (totalEquipmentHours > totalEmployeeHours + 0.05) {
      newErrors.push(t.equipmentExceedsEmployee);
    }

    // Check superintendent info — optional when no one is on site to sign
    // (unknown super/email should be submitted blank, not faked to pass validation)
    if (!noOneToSign && !superintendentName.trim()) {
      newErrors.push(t.nameRequiredError);
    }

    if (!noOneToSign && !superintendentEmail.trim()) {
      newErrors.push(t.emailRequiredError);
    } else if (superintendentEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(superintendentEmail)) {
        newErrors.push(t.invalidEmailError);
      }
    }

    // Note: Signature is NOT required for preview

    setErrors(newErrors);
    if (newErrors.length > 0) {
      setShowErrorModal(true);
    }
    return newErrors.length === 0;
  }, [employees, totalEquipmentHours, totalEmployeeHours, superintendentName, superintendentEmail, noOneToSign, t]);

  // AI suggest function - translate and fix text
  const handleAiSuggest = async (text: string, setTranslating: (v: boolean) => void, setText: (v: string) => void) => {
    if (!text.trim()) return;

    setTranslating(true);
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/translate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });

      const result = await response.json();
      if (result.success && result.data?.improved) {
        setText(result.data.improved);
      }
    } catch (error) {
      console.error('Error translating text:', error);
    } finally {
      setTranslating(false);
    }
  };

  // Handle review for signature button - go to preview mode
  const handleReviewForSignature = () => {
    if (validateForPreview()) {
      window.scrollTo(0, 0);  // Scroll to top before showing preview
      setShowPreview(true);
    }
  };

  // Handle edit from preview - return to form
  const handleEditFromPreview = () => {
    setShowPreview(false);
  };

  // Handle sign and submit from preview
  const handleSignAndSubmit = async (signature: string) => {
    setSignatureBase64(signature);
    // Wait for state update then submit
    await submitTicket(signature);
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Core submit function - can be called with signature from preview or from form
  const submitTicket = async (signatureToSubmit?: string) => {
    const sig = signatureToSubmit || signatureBase64;

    // Signature required unless "no one to sign" is checked with a reason
    if (!sig && !(noOneToSign && unsignedReason)) {
      setErrors([t.signatureRequiredError]);
      setShowErrorModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('bfa_token');

      // Prepare employee data
      // General Laborer employee_id in database is 102 (inactive, won't show in lists)
      const GENERAL_LABORER_ID = 102;
      const employeeData = employees
        .filter(e => e.selected)
        .map(e => ({
          employee_id: e.employee_id < 0 ? GENERAL_LABORER_ID : e.employee_id,
          hours_worked: typeof e.tm_hours === 'string' ? parseFloat(e.tm_hours) || 0 : (e.tm_hours || 0)
        }));

      // Prepare equipment data (only entries with hours > 0)
      const equipmentData = equipmentEntries
        .filter(e => {
          const hours = typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0);
          return hours > 0;
        })
        .map(e => ({
          equipment_id: e.equipment_id,
          hours_used: typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0),
          is_rental: e.is_rental,
          rental_company: e.rental_company,
          rental_equipment_type: e.rental_equipment_type,
          equipment_name: e.equipment_name,
          equipment_number: e.equipment_number,
          is_mobilized: e.is_mobilized
        }));

      // Prepare material data - use custom_description when "Other" is selected
      const materialData = materials
        .filter(m => m.description.trim() && m.quantity > 0)
        .map(m => ({
          material_type: m.material_type,
          description: m.description === 'Other' ? m.custom_description : m.description,
          quantity: m.quantity,
          unit: m.unit
        }))
        .filter(m => m.description.trim()); // Filter out empty custom descriptions

      // Prepare dumpster data
      const dumpsterData = dumpsters
        .filter(d => d.quantity > 0)
        .map(d => ({
          dumpster_type: d.dumpster_type,
          size: d.size,
          quantity: d.quantity
        }));

      // Separate pre-uploaded photos from pending ones
      const preUploadedPhotos = photoUploads
        .filter(p => p.uploadStatus === 'uploaded' && p.uploadedUrl && attachments.includes(p.file))
        .map(p => ({ fullUrl: p.uploadedUrl!, thumbnailUrl: p.thumbnailUrl }));

      // Only convert pending photos to base64
      const pendingPhotos = attachments.filter(file => {
        const upload = photoUploads.find(p => p.file === file);
        return !upload || upload.uploadStatus !== 'uploaded';
      });

      const attachmentBase64s = await Promise.all(
        pendingPhotos.map(async (file) => ({
          filename: file.name,
          data: await fileToBase64(file)
        }))
      );

      console.log(`Submitting: ${preUploadedPhotos.length} pre-uploaded, ${pendingPhotos.length} pending photos`);

      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/form`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: jobId,
          employee_id: employeeId,
          employees: employeeData,
          equipment: equipmentData,
          materials: materialData,
          dumpsters: dumpsterData,
          superintendent_name: superintendentName.trim() || null,
          superintendent_email: superintendentEmail.trim() || null,
          signature_base64: sig || null,
          notes: notes,
          attachments: attachmentBase64s,
          pre_uploaded_photos: preUploadedPhotos,
          // New T&M enhancement fields
          work_description: workDescription,
          unsigned_reason: noOneToSign ? unsignedReason : null,
          // Build start_time as a proper local datetime - parse time and create Date in local timezone
          start_time: startTime ? (() => {
            const [hours, minutes] = startTime.split(':').map(Number);
            const localDate = new Date();
            localDate.setHours(hours, minutes, 0, 0);
            return localDate.toISOString(); // Converts local time to UTC ISO string correctly
          })() : null,
          stop_time: stopTime ? (() => {
            const [hours, minutes] = stopTime.split(':').map(Number);
            const localDate = new Date();
            localDate.setHours(hours, minutes, 0, 0);
            return localDate.toISOString();
          })() : null
        })
      });

      const result = await response.json();

      if (result.success) {
        clearDraft();
        setShowPreview(false);
        onSubmit(result.data.ticket_number);
      } else {
        setErrors([result.error || 'Failed to submit ticket']);
        setShowErrorModal(true);
      }
    } catch (error: any) {
      console.error('Error submitting T&M ticket:', error);
      const errorMsg = error.message || 'Failed to submit ticket';

      // Check for entity too large error - don't queue these
      if (errorMsg.includes('413') || errorMsg.toLowerCase().includes('too large') || errorMsg.toLowerCase().includes('entity')) {
        setErrors([language === 'en'
          ? 'Photos are too large. Please remove some photos and try again.'
          : 'Las fotos son demasiado grandes. Por favor elimine algunas fotos e intente de nuevo.']);
        setShowErrorModal(true);
      } else {
        // Network error or server unavailable - queue for offline sync
        const isNetworkError = !navigator.onLine ||
          errorMsg.toLowerCase().includes('network') ||
          errorMsg.toLowerCase().includes('fetch') ||
          errorMsg.toLowerCase().includes('failed to fetch') ||
          errorMsg.toLowerCase().includes('timeout') ||
          error.name === 'TypeError'; // fetch throws TypeError when offline

        if (isNetworkError) {
          try {
            // Prepare employee data for queueing
            const GENERAL_LABORER_ID = 102;
            const employeeData = employees
              .filter(e => e.selected)
              .map(e => ({
                employee_id: e.employee_id < 0 ? GENERAL_LABORER_ID : e.employee_id,
                hours_worked: typeof e.tm_hours === 'string' ? parseFloat(e.tm_hours) || 0 : (e.tm_hours || 0)
              }));

            // Prepare equipment data
            const equipmentData = equipmentEntries
              .filter(e => {
                const hours = typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0);
                return hours > 0;
              })
              .map(e => ({
                equipment_id: e.equipment_id,
                hours_used: typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0),
                is_rental: e.is_rental,
                rental_company: e.rental_company,
                rental_equipment_type: e.rental_equipment_type,
                equipment_name: e.equipment_name,
                equipment_number: e.equipment_number,
                is_mobilized: e.is_mobilized
              }));

            // Prepare material data
            const materialData = materials
              .filter(m => m.description.trim() && m.quantity > 0)
              .map(m => ({
                material_type: m.material_type,
                description: m.description === 'Other' ? m.custom_description : m.description,
                quantity: m.quantity,
                unit: m.unit
              }))
              .filter(m => m.description.trim());

            // Prepare dumpster data
            const dumpsterData = dumpsters
              .filter(d => d.quantity > 0)
              .map(d => ({
                dumpster_type: d.dumpster_type,
                size: d.size,
                quantity: d.quantity
              }));

            // Separate pre-uploaded photos from pending ones
            const preUploadedPhotos = photoUploads
              .filter(p => p.uploadStatus === 'uploaded' && p.uploadedUrl && attachments.includes(p.file))
              .map(p => ({ fullUrl: p.uploadedUrl!, thumbnailUrl: p.thumbnailUrl }));

            // Only convert pending photos to base64
            const pendingPhotos = attachments.filter(file => {
              const upload = photoUploads.find(p => p.file === file);
              return !upload || upload.uploadStatus !== 'uploaded';
            });

            const attachmentBase64s = await Promise.all(
              pendingPhotos.map(async (file) => ({
                filename: file.name,
                data: await fileToBase64(file)
              }))
            );

            // Queue the ticket for later sync
            // Capture original creation timestamp so server uses correct date even if synced later
            const originalCreatedAt = new Date().toISOString();
            await offlineService.queueTMTicket({
              job_id: jobId,
              job_name: jobName,
              employee_id: employeeId,
              ticket_data: {
                employees: employeeData,
                equipment: equipmentData,
                materials: materialData,
                dumpsters: dumpsterData,
                superintendent_name: superintendentName.trim() || null,
                superintendent_email: superintendentEmail.trim() || null,
                signature_base64: sig || null,
                notes: notes,
                work_description: workDescription,
                unsigned_reason: noOneToSign ? unsignedReason : null,
                start_time: startTime ? (() => {
                  const [hours, minutes] = startTime.split(':').map(Number);
                  const localDate = new Date();
                  localDate.setHours(hours, minutes, 0, 0);
                  return localDate.toISOString();
                })() : null,
                stop_time: stopTime ? (() => {
                  const [hours, minutes] = stopTime.split(':').map(Number);
                  const localDate = new Date();
                  localDate.setHours(hours, minutes, 0, 0);
                  return localDate.toISOString();
                })() : null,
                attachments: attachmentBase64s,
                pre_uploaded_photos: preUploadedPhotos,
                // Original timestamp - server uses this for created_at instead of GETDATE()
                original_created_at: originalCreatedAt
              },
              created_at: originalCreatedAt,
              retry_count: 0
            });

            // Clear the draft since it's now queued
            clearDraft();
            setShowPreview(false);

            // Show success message for queued ticket
            onSubmit(`QUEUED-${Date.now()}`);
          } catch (queueError) {
            console.error('Failed to queue T&M ticket:', queueError);
            setErrors([language === 'en'
              ? 'Failed to save ticket for later. Please try again.'
              : 'Error al guardar el ticket para más tarde. Intente de nuevo.']);
            setShowErrorModal(true);
          }
        } else {
          setErrors([errorMsg]);
          setShowErrorModal(true);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Legacy submit handler (kept for direct form submission if needed)
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    await submitTicket();
  };

  // Section header component
  const SectionHeader: React.FC<{
    title: string;
    icon: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    badge?: React.ReactNode;
  }> = ({ title, icon, expanded, onToggle, badge }) => (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        borderBottom: expanded ? '1px solid #e5e7eb' : 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {icon}
        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>{title}</span>
        {badge !== undefined && (
          <span style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {badge}
          </span>
        )}
      </div>
      {expanded ? <ChevronUp size={20} color="#6b7280" /> : <ChevronDown size={20} color="#6b7280" />}
    </button>
  );

  // Prepare data for preview
  const previewEmployees = useMemo(() => {
    return employees
      .filter(e => e.selected)
      .map(e => ({
        name: `${e.first_name} ${e.last_name}`,
        role: e.role,
        hours: typeof e.tm_hours === 'string' ? parseFloat(e.tm_hours) || 0 : (e.tm_hours || 0)
      }));
  }, [employees]);

  const previewEquipment = useMemo(() => {
    return equipmentEntries
      .filter(e => {
        const hours = typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0);
        return hours > 0;
      })
      .map(e => ({
        name: e.equipment_number ? `${e.equipment_number} ${e.equipment_name}` : e.equipment_name,
        type: e.is_rental ? e.rental_equipment_type : (e.equipment_type || ''),
        hours: typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0),
        isMobilized: e.is_mobilized,
        isRental: e.is_rental,
        rentalCompany: e.rental_company
      }));
  }, [equipmentEntries]);

  const previewMaterials = useMemo(() => {
    return materials
      .filter(m => m.description.trim() && m.quantity > 0)
      .map(m => ({
        type: m.material_type,
        description: m.description === 'Other' ? m.custom_description : m.description,
        quantity: m.quantity,
        unit: m.unit
      }))
      .filter(m => m.description.trim());
  }, [materials]);

  const previewDumpsters = useMemo(() => {
    return dumpsters
      .filter(d => d.quantity > 0)
      .map(d => ({
        dumpster_type: d.dumpster_type,
        size: d.size,
        quantity: d.quantity
      }));
  }, [dumpsters]);

  // Show loading screen while data is loading
  if (loadingEmployees || loadingEquipment) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '40px 20px'
      }}>
        <Loader2 size={48} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{
          marginTop: '20px',
          fontSize: '18px',
          color: '#4b5563',
          fontWeight: '500'
        }}>
          {language === 'en' ? 'Loading T&M Ticket...' : 'Cargando Boleto T&M...'}
        </p>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show preview if in preview mode
  if (showPreview) {
    return (
      <TMTicketPreview
        jobName={jobName}
        jobNumber={jobNumber}
        submittedBy={submitterName}
        date={new Date()}
        superintendentName={superintendentName}
        superintendentEmail={superintendentEmail}
        employees={previewEmployees}
        equipment={previewEquipment}
        materials={previewMaterials}
        dumpsters={previewDumpsters}
        notes={notes}
        totalEmployeeHours={totalEmployeeHours}
        totalEquipmentHours={totalEquipmentHours}
        language={language}
        onEdit={handleEditFromPreview}
        onSign={handleSignAndSubmit}
        workDescription={workDescription}
        gcName={gcName}
        startTime={startTime ? (() => { const [h, m] = startTime.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0); return d; })() : undefined}
        stopTime={stopTime ? (() => { const [h, m] = stopTime.split(':').map(Number); const d = new Date(); d.setHours(h, m, 0, 0); return d; })() : undefined}
      />
    );
  }

  return (
    <div>
      {/* Job Name Header - Simple */}
      <div style={{
        paddingBottom: '12px',
        paddingLeft: '4px',
        paddingRight: '4px',
        marginBottom: '12px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
          {jobName}
        </div>
      </div>

      {/* Start Time Input */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>
            {language === 'en' ? 'Start Time' : 'Hora de Inicio'}
          </label>
          <CustomTimePicker
            value={startTime}
            onChange={(v) => { setStartTime(v); if (stopTime) applyStopTimeToHours(v, stopTime); }}
            label={language === 'en' ? 'Start Time' : 'Hora de Inicio'}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              color: '#1f2937',
              backgroundColor: '#f9fafb',
              minWidth: '150px'
            }}
          />
        </div>
        {/* Stop Time — sets every selected worker's hours to the span */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
          <div>
            <label style={{ fontSize: '15px', fontWeight: '600', color: '#374151', display: 'block' }}>
              {language === 'en' ? 'Stop Time' : 'Hora de Fin'}
            </label>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              {spanHours(startTime, stopTime) != null
                ? (language === 'en'
                    ? `${spanHours(startTime, stopTime)!.toFixed(1)} hrs applied to crew`
                    : `${spanHours(startTime, stopTime)!.toFixed(1)} hrs aplicadas al equipo`)
                : (language === 'en' ? 'Optional — sets crew hours' : 'Opcional — fija horas del equipo')}
            </span>
          </div>
          <CustomTimePicker
            value={stopTime}
            onChange={(v) => { setStopTime(v); applyStopTimeToHours(startTime, v); }}
            label={language === 'en' ? 'Stop Time' : 'Hora de Fin'}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              color: '#1f2937',
              backgroundColor: '#f9fafb',
              minWidth: '150px'
            }}
          />
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && errors.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle size={28} color="#ef4444" />
              </div>
              <h3 style={{ margin: 0, color: '#991b1b', fontSize: '20px' }}>{t.validationError}</h3>
            </div>
            <ul style={{ margin: '0 0 20px 0', paddingLeft: '24px', color: '#7f1d1d' }}>
              {errors.map((error, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>{error}</li>
              ))}
            </ul>
            <button
              onClick={() => {
                setShowErrorModal(false);
                setErrors([]);
              }}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {language === 'en' ? 'OK' : 'Aceptar'}
            </button>
          </div>
        </div>
      )}

      {/* Adjusted Hours Modal */}
      {adjustedHoursMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '340px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <AlertCircle size={28} color="#d97706" />
            </div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px'
            }}>
              {language === 'en' ? 'Hours Adjusted' : 'Horas Ajustadas'}
            </h3>
            <p style={{
              color: '#4b5563',
              fontSize: '15px',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              {adjustedHoursMessage}
            </p>
            <button
              type="button"
              onClick={() => setAdjustedHoursMessage(null)}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Employee Hours Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.employeeHours}
          icon={<Users size={20} color="#3b82f6" />}
          expanded={expandedSections.employees}
          onToggle={() => toggleSection('employees')}
          badge={employees.filter(e => e.selected).length}
        />

        {expandedSections.employees && (
          <div style={{ padding: '16px' }}>
            {loadingEmployees ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <p>{t.loadingEmployees}</p>
              </div>
            ) : employees.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                {t.noEmployees}
              </p>
            ) : (
              <>
                {/* Select All / Deselect All / Add Laborer */}
                <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => toggleAllEmployees(true)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#eff6ff',
                      color: '#3b82f6',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {t.selectAll}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleAllEmployees(false)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f9fafb',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    {t.deselectAll}
                  </button>
                  <button
                    type="button"
                    onClick={addGeneralLaborer}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={16} />
                    {t.addLaborer}
                  </button>
                </div>

                {/* Employee List */}
                {employees.map(emp => {
                  const isManualLaborer = emp.employee_id < 0;
                  return (
                    <div
                      key={emp.employee_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: isManualLaborer ? '#fef3c7' : (emp.selected ? '#f0fdf4' : '#f9fafb'),
                        border: `1px solid ${isManualLaborer ? '#f59e0b' : (emp.selected ? '#22c55e' : '#e5e7eb')}`,
                        borderRadius: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      {!isManualLaborer && (
                        <input
                          type="checkbox"
                          checked={emp.selected}
                          onChange={() => toggleEmployee(emp.employee_id)}
                          style={{ width: '20px', height: '20px', accentColor: '#22c55e' }}
                        />
                      )}
                      {isManualLaborer && (
                        <button
                          type="button"
                          onClick={() => removeManualLaborer(emp.employee_id)}
                          style={{
                            padding: '4px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                      <div style={{ flex: 1 }}>
                        {isManualLaborer ? (
                          <input
                            type="text"
                            value={`${emp.first_name} ${emp.last_name}`.trim()}
                            onFocus={(e) => {
                              e.target.select();
                              scrollInputIntoView(e.target);
                            }}
                            onChange={(e) => {
                              const parts = e.target.value.split(' ');
                              const firstName = parts[0] || '';
                              const lastName = parts.slice(1).join(' ') || '';
                              updateLaborerName(emp.employee_id, firstName, lastName);
                            }}
                            placeholder="Enter name..."
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '15px',
                              fontWeight: '600',
                              width: '100%',
                              maxWidth: '180px',
                              backgroundColor: 'white'
                            }}
                          />
                        ) : (
                          <>
                            <div style={{ fontWeight: '600', color: '#1f2937' }}>
                              {emp.first_name} {emp.last_name}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {emp.role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} - {emp.hours_worked.toFixed(1)} hrs today
                            </div>
                          </>
                        )}
                        {isManualLaborer && (
                          <div style={{ fontSize: '11px', color: '#92400e', marginTop: '2px' }}>
                            Manual Entry
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={emp.tm_hours}
                          onFocus={(e) => {
                            // Select all text on focus so user can immediately type new value
                            e.target.select();
                            // Scroll input into view only if keyboard opens
                            scrollInputIntoView(e.target);
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty, numbers, and decimal formats like ".5", "0.", "1.5"
                            if (val === '' || /^\.?\d*\.?\d*$/.test(val)) {
                              updateEmployeeHours(emp.employee_id, val);
                            }
                          }}
                          onBlur={(e) => {
                            // On blur, clean up the value to a proper number
                            const parsed = parseFloat(e.target.value);
                            if (!isNaN(parsed)) {
                              updateEmployeeHours(emp.employee_id, roundUpHalf(parsed));
                            } else if (e.target.value === '' || e.target.value === '.') {
                              updateEmployeeHours(emp.employee_id, 0);
                            }
                          }}
                          onContextMenu={(e) => e.preventDefault()}
                          disabled={!isManualLaborer && !emp.selected}
                          style={{
                            width: '70px',
                            padding: '8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '16px',
                            textAlign: 'center',
                            backgroundColor: (isManualLaborer || emp.selected) ? 'white' : '#f3f4f6',
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                            WebkitTouchCallout: 'none'
                          }}
                        />
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>{t.hours}</span>
                      </div>
                    </div>
                  );
                })}

                {/* Total Employee Hours */}
                <div style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{t.totalEmployeeHours}</span>
                  <span style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>{totalEmployeeHours.toFixed(1)}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Equipment Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <SectionHeader
          title={t.equipment}
          icon={<Wrench size={20} color="#f59e0b" />}
          expanded={expandedSections.equipment}
          onToggle={() => toggleSection('equipment')}
          badge={equipmentEntries.filter(e => {
            const hours = typeof e.hours_used === 'string' ? parseFloat(e.hours_used) || 0 : (e.hours_used || 0);
            return hours > 0;
          }).length}
        />

        {expandedSections.equipment && (
          <div style={{ padding: '16px', paddingTop: '60px' }}>
            {/* Available Hours Banner - Fixed below header */}
            <div style={{
              position: 'fixed',
              top: '56px',
              left: '0',
              right: '0',
              zIndex: 100,
              padding: '12px 20px',
              backgroundColor: availableHours <= 0 ? '#dc2626' : '#16a34a',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                {t.availableHours}:
              </span>
              <span style={{
                fontSize: '24px',
                fontWeight: '700',
                color: 'white'
              }}>
                {availableHours.toFixed(1)} hrs
              </span>
            </div>

            {loadingEquipment ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <p>{t.loadingEquipment}</p>
              </div>
            ) : (
              <>
                {/* Equipment Entries */}
                {equipmentEntries.map(entry => (
                  <div
                    key={entry.id}
                    style={{
                      padding: '12px',
                      backgroundColor: entry.is_rental ? '#fef3c7' : '#f9fafb',
                      border: `1px solid ${entry.is_rental ? '#fbbf24' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}
                  >
                    {/* Equipment Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div>
                        {entry.isManual ? (
                          <input
                            type="text"
                            value={entry.equipment_name}
                            onChange={(e) => updateEquipmentEntry(entry.id, 'equipment_name', e.target.value)}
                            placeholder={t.equipmentName}
                            style={{
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              width: '200px'
                            }}
                          />
                        ) : (
                          <div>
                            <div style={{ fontWeight: '600', color: '#1f2937' }}>
                              {entry.equipment_number && <span>{entry.equipment_number} </span>}
                              {entry.equipment_name}
                            </div>
                            {entry.equipment_type && (
                              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                {entry.equipment_type}
                              </div>
                            )}
                          </div>
                        )}
                        {entry.is_rental && (
                          <span style={{
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            RENTAL
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeEquipmentEntry(entry.id)}
                        style={{
                          padding: '6px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Rental Fields */}
                    {entry.is_rental && entry.isManual && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
                        {/* Rental Company Dropdown */}
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                            {t.rentalCompany}
                          </label>
                          <select
                            value={entry.rental_company === '' ? '' : (RENTAL_COMPANIES.includes(entry.rental_company) ? entry.rental_company : 'Other')}
                            onChange={(e) => {
                              if (e.target.value === 'Other') {
                                updateEquipmentEntry(entry.id, 'rental_company', 'Other');
                              } else {
                                updateEquipmentEntry(entry.id, 'rental_company', e.target.value);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '16px',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t.selectRentalCompany}</option>
                            {RENTAL_COMPANIES.map(company => (
                              <option key={company} value={company}>{company}</option>
                            ))}
                          </select>
                          {/* Custom input when Other is selected */}
                          {entry.rental_company === 'Other' && (
                            <input
                              type="text"
                              value=""
                              onFocus={(e) => {
                                scrollInputIntoView(e.target);
                              }}
                              onChange={(e) => updateEquipmentEntry(entry.id, 'rental_company', e.target.value || 'Other')}
                              placeholder={t.customRentalCompany}
                              style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '16px'
                              }}
                            />
                          )}
                          {/* Show custom value input when a custom company was entered */}
                          {entry.rental_company !== '' && entry.rental_company !== 'Other' && !RENTAL_COMPANIES.includes(entry.rental_company) && (
                            <input
                              type="text"
                              value={entry.rental_company}
                              onFocus={(e) => {
                                scrollInputIntoView(e.target);
                              }}
                              onChange={(e) => updateEquipmentEntry(entry.id, 'rental_company', e.target.value || 'Other')}
                              placeholder={t.customRentalCompany}
                              style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '16px'
                              }}
                            />
                          )}
                        </div>

                        {/* Equipment Type Dropdown */}
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                            {t.equipmentType}
                          </label>
                          <select
                            value={entry.rental_equipment_type === '' ? '' : (EQUIPMENT_TYPES.includes(entry.rental_equipment_type) ? entry.rental_equipment_type : 'Other')}
                            onChange={(e) => {
                              if (e.target.value === 'Other') {
                                updateEquipmentEntry(entry.id, 'rental_equipment_type', 'Other');
                                updateEquipmentEntry(entry.id, 'equipment_name', '');
                              } else {
                                updateEquipmentEntry(entry.id, 'rental_equipment_type', e.target.value);
                                updateEquipmentEntry(entry.id, 'equipment_name', e.target.value);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '16px',
                              backgroundColor: 'white'
                            }}
                          >
                            <option value="">{t.selectEquipmentType}</option>
                            {EQUIPMENT_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          {/* Custom input when Other is selected */}
                          {entry.rental_equipment_type === 'Other' && (
                            <input
                              type="text"
                              value=""
                              onFocus={(e) => {
                                scrollInputIntoView(e.target);
                              }}
                              onChange={(e) => {
                                const val = e.target.value || 'Other';
                                updateEquipmentEntry(entry.id, 'rental_equipment_type', val);
                                updateEquipmentEntry(entry.id, 'equipment_name', val === 'Other' ? '' : val);
                              }}
                              placeholder={t.customEquipmentType}
                              style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '16px'
                              }}
                            />
                          )}
                          {/* Show custom value when a custom type was entered */}
                          {entry.rental_equipment_type !== '' && entry.rental_equipment_type !== 'Other' && !EQUIPMENT_TYPES.includes(entry.rental_equipment_type) && (
                            <input
                              type="text"
                              value={entry.rental_equipment_type}
                              onFocus={(e) => {
                                scrollInputIntoView(e.target);
                              }}
                              onChange={(e) => {
                                const val = e.target.value || 'Other';
                                updateEquipmentEntry(entry.id, 'rental_equipment_type', val);
                                updateEquipmentEntry(entry.id, 'equipment_name', val === 'Other' ? '' : val);
                              }}
                              placeholder={t.customEquipmentType}
                              style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '16px'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Manual Equipment Number */}
                    {entry.isManual && !entry.is_rental && (
                      <div style={{ marginBottom: '12px' }}>
                        <input
                          type="text"
                          value={entry.equipment_number}
                          onFocus={(e) => {
                            scrollInputIntoView(e.target);
                          }}
                          onChange={(e) => updateEquipmentEntry(entry.id, 'equipment_number', e.target.value)}
                          placeholder={t.equipmentNumber}
                          style={{
                            padding: '8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '150px'
                          }}
                        />
                      </div>
                    )}

                    {/* Hours and Mobilization Row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={entry.hours_used}
                          onFocus={(e) => {
                            // Select all text on focus so user can immediately type new value
                            e.target.select();
                            // Scroll input into view only if keyboard opens
                            scrollInputIntoView(e.target);
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow empty, numbers, and decimal formats like ".5", "0.", "1.5"
                            if (val === '' || /^\.?\d*\.?\d*$/.test(val)) {
                              updateEquipmentEntry(entry.id, 'hours_used', val);
                            }
                          }}
                          onBlur={(e) => {
                            // On blur, clean up the value and auto-adjust if exceeds limit
                            let parsed = parseFloat(e.target.value);
                            if (isNaN(parsed) || e.target.value === '' || e.target.value === '.') {
                              parsed = 0;
                            }
                            parsed = roundUpHalf(parsed); // half-hour increments, rounded up

                            // Calculate what total would be with this value
                            const otherEquipmentHours = equipmentEntries
                              .filter(eq => eq.id !== entry.id)
                              .reduce((sum, eq) => {
                                const hrs = typeof eq.hours_used === 'string' ? parseFloat(eq.hours_used) || 0 : (eq.hours_used || 0);
                                return sum + hrs;
                              }, 0);

                            const maxAllowed = Math.max(0, totalEmployeeHours - otherEquipmentHours);

                            if (parsed > maxAllowed) {
                              // Auto-adjust to max allowed (floor to avoid exceeding limit)
                              const adjusted = Math.floor(maxAllowed * 2) / 2; // Floor to half hour
                              updateEquipmentEntry(entry.id, 'hours_used', adjusted);
                              setAdjustedHoursMessage(
                                language === 'en'
                                  ? `Hours adjusted from ${parsed} to ${adjusted} (max available)`
                                  : `Horas ajustadas de ${parsed} a ${adjusted} (máximo disponible)`
                              );
                            } else {
                              updateEquipmentEntry(entry.id, 'hours_used', parsed);
                            }
                          }}
                          onContextMenu={(e) => e.preventDefault()}
                          style={{
                            width: '70px',
                            padding: '8px',
                            border: '2px solid #f59e0b',
                            borderRadius: '6px',
                            fontSize: '16px',
                            textAlign: 'center',
                            backgroundColor: '#fffbeb',
                            WebkitUserSelect: 'none',
                            userSelect: 'none',
                            WebkitTouchCallout: 'none',
                            color: '#b45309',
                            fontWeight: '600'
                          }}
                        />
                        <span style={{ color: '#f59e0b', fontSize: '14px', fontWeight: '600' }}>{t.hours}</span>
                        {entry.gps_hours != null && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#dcfce7',
                            color: '#15803d',
                            border: '1px solid #86efac',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            <MapPin size={12} />
                            {t.gpsSuggested}: {entry.gps_hours.toFixed(1)}
                          </span>
                        )}
                      </div>

                      {/* Mobilization Checkbox */}
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: entry.is_mobilized ? '#dbeafe' : '#f3f4f6',
                        border: `2px solid ${entry.is_mobilized ? '#3b82f6' : '#d1d5db'}`,
                        borderRadius: '10px',
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: '200px'
                      }}>
                        <input
                          type="checkbox"
                          checked={entry.is_mobilized}
                          onChange={(e) => updateEquipmentEntry(entry.id, 'is_mobilized', e.target.checked)}
                          style={{ width: '28px', height: '28px', accentColor: '#3b82f6', flexShrink: 0 }}
                        />
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '16px', color: '#1f2937' }}>
                            <Truck size={18} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'text-bottom' }} />
                            {t.mobilized}
                          </div>
                          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                            {t.mobilizedHint}
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                ))}

                {/* Add/Search Equipment Section */}
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  backgroundColor: '#fffbeb',
                  border: '2px solid #f59e0b',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)'
                }}>
                  <div style={{ position: 'relative' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#92400e',
                      marginBottom: '8px'
                    }}>
                      {t.addEquipment}
                    </label>
                    <input
                      type="text"
                      value={equipmentSearch}
                      onChange={(e) => setEquipmentSearch(e.target.value)}
                      onFocus={(e) => {
                        // Scroll input into view when focused
                        scrollInputIntoView(e.target);
                      }}
                      placeholder={t.searchEquipment}
                      style={{
                        width: '100%',
                        padding: '14px',
                        fontSize: '16px',
                        border: '2px solid #d97706',
                        borderRadius: '8px',
                        backgroundColor: 'white'
                      }}
                    />

                    {/* Search Results Dropdown */}
                    {filteredEquipment.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: 'white',
                        border: '2px solid #d97706',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      }}>
                        {filteredEquipment.map(eq => (
                          <div
                            key={eq.equipment_id}
                            onClick={() => addEquipmentFromSearch(eq)}
                            style={{
                              padding: '12px',
                              borderBottom: '1px solid #e5e7eb',
                              cursor: 'pointer'
                            }}
                          >
                            <div style={{ fontWeight: '600', fontSize: '16px' }}>
                              {(eq.equipment_number || eq.bradanna_equipment_number) && (
                                <span>{eq.equipment_number || eq.bradanna_equipment_number} </span>
                              )}
                              {eq.equipment_name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {equipmentSearch.trim() && filteredEquipment.length === 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        padding: '12px',
                        backgroundColor: 'white',
                        border: '2px solid #d97706',
                        borderRadius: '8px',
                        color: '#6b7280',
                        fontSize: '14px',
                        zIndex: 1000
                      }}>
                        {t.noEquipmentFound}
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Rental Button */}
                <button
                  type="button"
                  onClick={addRentalEquipment}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={18} />
                  {t.addRental}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Work Description Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.workDescription}
          icon={<FileText size={20} color="#3b82f6" />}
          expanded={expandedSections.notes}
          onToggle={() => toggleSection('notes')}
          badge={workDescription.trim() ? <Check size={14} /> : undefined}
        />

        {expandedSections.notes && (
          <div style={{ padding: '16px' }}>
            <textarea
              value={workDescription}
              onFocus={(e) => {
                scrollInputIntoView(e.target);
              }}
              onChange={(e) => setWorkDescription(e.target.value)}
              placeholder={t.workDescriptionPlaceholder}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            {workDescription.trim() && (
              <button
                type="button"
                onClick={() => handleAiSuggest(workDescription, setIsTranslatingDescription, setWorkDescription)}
                disabled={isTranslatingDescription}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  backgroundColor: isTranslatingDescription ? '#9ca3af' : '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isTranslatingDescription ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isTranslatingDescription ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {t.translating}
                  </>
                ) : (
                  <>✨ {t.aiSuggest}</>
                )}
              </button>
            )}

            {/* GC Name Display */}
            {gcName && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>
                  {t.gcName}
                </div>
                <div style={{ fontSize: '14px', color: '#15803d' }}>{gcName}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.notes}
          icon={<FileText size={20} color="#6b7280" />}
          expanded={expandedSections.notes}
          onToggle={() => toggleSection('notes')}
        />

        {expandedSections.notes && (
          <div style={{ padding: '16px' }}>
            <textarea
              value={notes}
              onFocus={(e) => {
                scrollInputIntoView(e.target);
              }}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
            {notes.trim() && (
              <button
                type="button"
                onClick={() => handleAiSuggest(notes, setIsTranslatingNotes, setNotes)}
                disabled={isTranslatingNotes}
                style={{
                  marginTop: '8px',
                  padding: '10px 16px',
                  backgroundColor: isTranslatingNotes ? '#9ca3af' : '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isTranslatingNotes ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isTranslatingNotes ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {t.translating}
                  </>
                ) : (
                  <>✨ {t.aiSuggest}</>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Materials Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.materials}
          icon={<Package size={20} color="#8b5cf6" />}
          expanded={expandedSections.materials}
          onToggle={() => toggleSection('materials')}
          badge={materials.length > 0 ? materials.length : undefined}
        />

        {expandedSections.materials && (
          <div style={{ padding: '16px' }}>
            {/* Imports */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                {t.imports}
              </h4>
              {materials.filter(m => m.material_type === 'import').map(mat => (
                <div key={mat.id} style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: mat.description === 'Other' ? '8px' : '0',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <select
                      value={mat.description}
                      onChange={(e) => {
                        updateMaterial(mat.id, 'description', e.target.value);
                        if (e.target.value !== 'Other') {
                          updateMaterial(mat.id, 'custom_description', '');
                        }
                      }}
                      style={{
                        flex: 2,
                        minWidth: '150px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">{t.selectMaterial}</option>
                      {IMPORT_MATERIALS.map(material => (
                        <option key={material} value={material}>{material}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={mat.quantity || ''}
                      onFocus={(e) => {
                        scrollInputIntoView(e.target);
                        e.target.select();
                      }}
                      onChange={(e) => updateMaterial(mat.id, 'quantity', parseFloat(e.target.value) || 0)}
                      onContextMenu={(e) => e.preventDefault()}
                      placeholder={t.quantity}
                      style={{
                        width: '80px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        textAlign: 'center',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTouchCallout: 'none'
                      }}
                    />
                    <select
                      value={mat.unit}
                      onChange={(e) => updateMaterial(mat.id, 'unit', e.target.value)}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      {UNIT_OPTIONS.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeMaterial(mat.id)}
                      style={{
                        padding: '8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {mat.description === 'Other' && (
                    <input
                      type="text"
                      value={mat.custom_description}
                      onFocus={(e) => {
                        scrollInputIntoView(e.target);
                      }}
                      onChange={(e) => updateMaterial(mat.id, 'custom_description', e.target.value)}
                      placeholder={t.customMaterial}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addMaterial('import')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                {t.addImport}
              </button>
            </div>

            {/* Exports */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>
                {t.exports}
              </h4>
              {materials.filter(m => m.material_type === 'export').map(mat => (
                <div key={mat.id} style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '1px solid #fecaca'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: mat.description === 'Other' ? '8px' : '0',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <select
                      value={mat.description}
                      onChange={(e) => {
                        updateMaterial(mat.id, 'description', e.target.value);
                        if (e.target.value !== 'Other') {
                          updateMaterial(mat.id, 'custom_description', '');
                        }
                      }}
                      style={{
                        flex: 2,
                        minWidth: '150px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">{t.selectMaterial}</option>
                      {EXPORT_MATERIALS.map(material => (
                        <option key={material} value={material}>{material}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={mat.quantity || ''}
                      onFocus={(e) => {
                        scrollInputIntoView(e.target);
                        e.target.select();
                      }}
                      onChange={(e) => updateMaterial(mat.id, 'quantity', parseFloat(e.target.value) || 0)}
                      onContextMenu={(e) => e.preventDefault()}
                      placeholder={t.quantity}
                      style={{
                        width: '80px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        textAlign: 'center',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTouchCallout: 'none'
                      }}
                    />
                    <select
                      value={mat.unit}
                      onChange={(e) => updateMaterial(mat.id, 'unit', e.target.value)}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      {UNIT_OPTIONS.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeMaterial(mat.id)}
                      style={{
                        padding: '8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {mat.description === 'Other' && (
                    <input
                      type="text"
                      value={mat.custom_description}
                      onFocus={(e) => {
                        scrollInputIntoView(e.target);
                      }}
                      onChange={(e) => updateMaterial(mat.id, 'custom_description', e.target.value)}
                      placeholder={t.customMaterial}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addMaterial('export')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                {t.addExport}
              </button>
            </div>

            {/* Dumpsters */}
            <div style={{ marginTop: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed', marginBottom: '8px' }}>
                🗑️ {t.dumpsters}
              </h4>
              {dumpsters.map(dump => (
                <div key={dump.id} style={{
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#f5f3ff',
                  borderRadius: '8px',
                  border: '1px solid #c4b5fd'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <select
                      value={dump.dumpster_type}
                      onChange={(e) => {
                        const newType = e.target.value as 'Concrete' | 'Dirt' | 'Vegetation' | 'C&D';
                        updateDumpster(dump.id, 'dumpster_type', newType);
                        // Update size to default for new type
                        const dumpsterConfig = DUMPSTER_TYPES.find(d => d.type === newType);
                        if (dumpsterConfig) {
                          updateDumpster(dump.id, 'size', dumpsterConfig.defaultSize);
                        }
                      }}
                      style={{
                        flex: 2,
                        minWidth: '120px',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      {DUMPSTER_TYPES.map(dt => (
                        <option key={dt.type} value={dt.type}>{dt.type}</option>
                      ))}
                    </select>
                    <select
                      value={dump.size}
                      onChange={(e) => updateDumpster(dump.id, 'size', e.target.value)}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="20">20 {t.yards}</option>
                      <option value="30">30 {t.yards}</option>
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '14px', color: '#374151' }}>Qty:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={dump.quantity || ''}
                        onFocus={(e) => {
                          e.target.select();
                          scrollInputIntoView(e.target);
                        }}
                        onChange={(e) => updateDumpster(dump.id, 'quantity', parseInt(e.target.value) || 0)}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{
                          width: '50px',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                          textAlign: 'center',
                          WebkitUserSelect: 'none',
                          userSelect: 'none',
                          WebkitTouchCallout: 'none'
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDumpster(dump.id)}
                      style={{
                        padding: '8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {DUMPSTER_TYPES.map(dt => (
                  <button
                    key={dt.type}
                    type="button"
                    onClick={() => addDumpster(dt.type as 'Concrete' | 'Dirt' | 'Vegetation' | 'C&D')}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Plus size={14} />
                    {dt.type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photos Section */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.photos}
          icon={<Image size={20} color="#06b6d4" />}
          expanded={expandedSections.photos}
          onToggle={() => toggleSection('photos')}
          badge={attachments.length > 0 ? attachments.length : undefined}
        />

        {expandedSections.photos && (
          <div style={{ padding: '16px' }}>
            {/* Camera and Gallery Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {/* Camera Button */}
              <button
                type="button"
                onClick={() => handleAddPhoto(true)}
                style={{
                  flex: 1,
                  border: '3px solid #3b82f6',
                  borderRadius: '12px',
                  padding: '20px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#eff6ff'
                }}
              >
                <Camera style={{ width: '36px', height: '36px', color: '#3b82f6', margin: '0 auto 8px' }} />
                <p style={{ fontSize: '16px', color: '#1e40af', margin: 0, fontWeight: '600' }}>
                  {language === 'en' ? 'Camera' : 'Cámara'}
                </p>
              </button>

              {/* Gallery Button */}
              <button
                type="button"
                onClick={() => handleAddPhoto(false)}
                style={{
                  flex: 1,
                  border: '3px solid #8b5cf6',
                  borderRadius: '12px',
                  padding: '20px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f5f3ff'
                }}
              >
                <Image style={{ width: '36px', height: '36px', color: '#8b5cf6', margin: '0 auto 8px' }} />
                <p style={{ fontSize: '16px', color: '#5b21b6', margin: 0, fontWeight: '600' }}>
                  {language === 'en' ? 'Gallery' : 'Galería'}
                </p>
              </button>
            </div>

            <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', marginBottom: '16px' }}>
              {language === 'en'
                ? 'Receipts, invoices, job site photos'
                : 'Recibos, facturas, fotos del sitio'}
            </p>

            {/* Photo Thumbnails Grid */}
            {attachments.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '10px',
                marginBottom: '16px'
              }}>
                {attachments.map((file, index) => {
                  const uploadInfo = photoUploads.find(p => p.file === file);
                  const uploadStatus = uploadInfo?.uploadStatus || 'pending';
                  return (
                    <div key={index} style={{
                      position: 'relative',
                      height: '100px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: uploadStatus === 'uploaded' ? '2px solid #22c55e' :
                              uploadStatus === 'uploading' ? '2px solid #3b82f6' : '2px solid #e5e7eb'
                    }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Attachment ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Upload status indicator */}
                      <div style={{
                        position: 'absolute',
                        bottom: '4px',
                        left: '4px',
                        backgroundColor: uploadStatus === 'uploaded' ? '#22c55e' :
                                        uploadStatus === 'uploading' ? '#3b82f6' : '#6b7280',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px'
                      }}>
                        {uploadStatus === 'uploaded' && <Check size={10} />}
                        {uploadStatus === 'uploading' && <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />}
                        {uploadStatus === 'uploaded' ? (language === 'en' ? 'Ready' : 'Listo') :
                         uploadStatus === 'uploading' ? (language === 'en' ? 'Uploading' : 'Subiendo') :
                         (language === 'en' ? 'Pending' : 'Pendiente')}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          width: '28px',
                          height: '28px',
                          padding: '0',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {attachments.length > 0 && (
              <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                {attachments.length} {language === 'en' ? 'photo(s) attached' : 'foto(s) adjuntas'}
                {photoUploads.filter(p => p.uploadStatus === 'uploaded').length > 0 && (
                  <span style={{ color: '#22c55e', marginLeft: '8px' }}>
                    ({photoUploads.filter(p => p.uploadStatus === 'uploaded').length} {language === 'en' ? 'pre-uploaded' : 'pre-subidas'})
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Superintendent Info Section (Signature captured in preview) */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <SectionHeader
          title={t.superintendentSignature}
          icon={<FileText size={20} color="#dc2626" />}
          expanded={expandedSections.signature}
          onToggle={() => toggleSection('signature')}
          badge={superintendentName && superintendentEmail ? <Check size={14} /> : undefined}
        />

        {expandedSections.signature && (
          <div style={{ padding: '16px' }}>
            {/* Superintendent Name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t.superintendentName} {noOneToSign ? `(${t.ifKnown})` : '*'}
              </label>
              <input
                type="text"
                value={superintendentName}
                onFocus={(e) => {
                  scrollInputIntoView(e.target);
                }}
                onChange={(e) => setSuperintendentName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* Superintendent Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {t.superintendentEmail} {noOneToSign ? `(${t.ifKnown})` : '*'}
              </label>
              <input
                type="email"
                value={superintendentEmail}
                onFocus={(e) => {
                  scrollInputIntoView(e.target);
                }}
                onChange={(e) => setSuperintendentEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            {/* No one to sign - Unsigned Ticket Option */}
            <div style={{
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: noOneToSign ? '#fef3c7' : '#f9fafb',
              border: `2px solid ${noOneToSign ? '#f59e0b' : '#e5e7eb'}`,
              borderRadius: '12px'
            }}>
              {/* Header explanation */}
              <div style={{
                marginBottom: '12px',
                fontSize: '13px',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                {language === 'en'
                  ? 'If the superintendent is not available to sign right now, you can submit the ticket without a signature. They will receive an email link to sign later.'
                  : 'Si el superintendente no esta disponible para firmar ahora, puede enviar el boleto sin firma. Recibira un enlace por correo para firmar despues.'}
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                padding: '12px',
                backgroundColor: noOneToSign ? '#fbbf24' : '#e5e7eb',
                borderRadius: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={noOneToSign}
                  onChange={(e) => {
                    setNoOneToSign(e.target.checked);
                    if (!e.target.checked) setUnsignedReason('');
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    accentColor: '#f59e0b'
                  }}
                />
                <span style={{ fontSize: '16px', fontWeight: '600', color: noOneToSign ? '#78350f' : '#374151' }}>
                  {t.noOneToSign}
                </span>
              </label>

              {noOneToSign && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#92400e',
                    marginBottom: '8px'
                  }}>
                    {t.unsignedReason} *
                  </label>
                  <CustomSelect
                    value={unsignedReason}
                    onChange={(value) => setUnsignedReason(value)}
                    placeholder={t.selectReason}
                    options={[
                      { value: 'Not available', label: t.notAvailable },
                      { value: 'Left jobsite', label: t.leftJobsite },
                      { value: 'Will sign later', label: t.willSignLater }
                    ]}
                    style={{
                      border: '2px solid #f59e0b',
                      borderRadius: '8px'
                    }}
                  />

                  {unsignedReason && (
                    <div style={{
                      marginTop: '16px',
                      padding: '16px',
                      backgroundColor: '#dcfce7',
                      borderRadius: '8px',
                      border: '1px solid #22c55e'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>
                        ✉️ {language === 'en' ? 'What happens next:' : 'Que pasa despues:'}
                      </div>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        fontSize: '13px',
                        color: '#15803d',
                        lineHeight: '1.8'
                      }}>
                        <li>{language === 'en' ? 'Ticket is saved as "unsigned"' : 'El boleto se guarda como "sin firma"'}</li>
                        <li>{language === 'en' ? 'Superintendent receives email with signing link' : 'El superintendente recibe un correo con enlace para firmar'}</li>
                        <li>{language === 'en' ? 'They can sign from their phone or computer' : 'Pueden firmar desde su telefono o computadora'}</li>
                        <li>{language === 'en' ? 'You can also get them to sign later in person' : 'Tambien puede obtener su firma despues en persona'}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Info about signature */}
            {!noOneToSign && (
              <div style={{
                padding: '12px',
                backgroundColor: '#eff6ff',
                border: '1px solid #93c5fd',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#1e40af'
              }}>
                {language === 'en'
                  ? 'Superintendent will sign on the preview screen after reviewing the ticket.'
                  : 'El superintendente firmara en la pantalla de vista previa despues de revisar el boleto.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit/Cancel Buttons */}
      <div style={{
        padding: '16px 0',
        display: 'flex',
        gap: '12px',
        marginBottom: '250px'
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSubmitting ? 'not-allowed' : 'pointer'
          }}
        >
          {t.cancel}
        </button>
        {noOneToSign && unsignedReason ? (
          // Submit without signature
          <button
            type="button"
            onClick={() => {
              if (validateForPreview()) {
                submitTicket();
              }
            }}
            disabled={isSubmitting}
            style={{
              flex: 2,
              padding: '14px',
              backgroundColor: isSubmitting ? '#9ca3af' : '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                {t.submitting}
              </>
            ) : (
              <>
                <Send size={20} />
                {language === 'en' ? 'Submit Without Signature' : 'Enviar Sin Firma'}
              </>
            )}
          </button>
        ) : (
          // Review for signature
          <button
            type="button"
            onClick={handleReviewForSignature}
            disabled={isSubmitting}
            style={{
              flex: 2,
              padding: '14px',
              backgroundColor: isSubmitting ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Eye size={20} />
            {t.reviewForSignature}
          </button>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TMTicketForm;
